import { z } from 'zod';
import { createRowSchema, propertySchema, propertiesSchema, updateDataSourceSchema, viewsSchema, } from './databases.js';
import { pageCoverSchema } from './pages.js';
import { createThreadSchema } from './comments.js';
import { createPageSchema, mcpPageEditSchema, mcpPageStructureSchema } from './pages.js';
/**
 * What a token may do. The five capabilities are the prototype's connection
 * checkboxes; MCP maps the first three onto its own tools.
 */
export const tokenScopeSchema = z.enum(['read', 'update', 'insert', 'rcom', 'icom']);
/** How much of the person behind a token the client may see. */
export const userInfoLevelSchema = z.enum(['none', 'noemail', 'email']);
export const createTokenSchema = z.object({
    name: z.string().trim().min(1).max(80),
    kind: z.enum(['token', 'connection']).default('token'),
    // A token without any capability would be a token that can do nothing.
    scopes: z.array(tokenScopeSchema).min(1).max(5).default(['read']),
    userInfo: userInfoLevelSchema.default('none'),
    /** Days until it expires; connections do not expire. */
    expiresInDays: z.number().int().min(1).max(365).optional(),
});
export const tokenViewSchema = z.object({
    id: z.string(),
    name: z.string(),
    kind: z.enum(['token', 'connection']),
    prefix: z.string(),
    scopes: z.array(tokenScopeSchema),
    userInfo: userInfoLevelSchema,
    expiresAt: z.string().nullable(),
    lastUsedAt: z.string().nullable(),
    revoked: z.boolean(),
    createdAt: z.string(),
});
/**
 * Events a webhook may subscribe to. `page.*` and `*` are wildcards; the list
 * mirrors what the workspace actually writes into the outbox.
 */
export const webhookEventSchema = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[a-z_]+(\.[a-z_*]+)?$/);
export const createWebhookSchema = z.object({
    name: z.string().trim().min(1).max(80),
    // Only https, and never a private address: a webhook makes the server call out.
    url: z
        .string()
        .trim()
        .min(1)
        .max(500)
        .refine((value) => value.startsWith('https://'), { message: 'url_must_be_https' }),
    events: z.array(webhookEventSchema).min(1).max(20),
});
export const webhookViewSchema = z.object({
    id: z.string(),
    name: z.string(),
    url: z.string(),
    events: z.array(webhookEventSchema),
    active: z.boolean(),
});
const mcpWorkspaceSchema = mcpPageStructureSchema.omit({ pageId: true });
const mcpPageSchema = mcpPageStructureSchema;
const mcpMarkdownSchema = z.string().max(100_000);
const mcpSourceSchema = mcpWorkspaceSchema.extend({ dataSourceId: z.string().uuid() });
export const mcpDatabaseArguments = {
    noti_list_members: mcpWorkspaceSchema,
    noti_get_database: mcpSourceSchema,
    noti_update_database: mcpSourceSchema.extend({
        ...updateDataSourceSchema.shape,
        properties: propertiesSchema.optional(),
        views: viewsSchema.optional(),
    }),
    noti_create_database_row: mcpSourceSchema.extend({
        ...createRowSchema.shape,
        markdown: mcpMarkdownSchema.optional(),
    }),
    noti_update_database_row: mcpSourceSchema.extend({
        rowId: z.string().uuid(),
        values: z
            .record(z.string().min(1).max(64), z.unknown())
            .refine((values) => Object.keys(values).length > 0, {
            message: 'At least one value is required',
        }),
    }),
    noti_set_database_row_trash: mcpSourceSchema.extend({
        rowId: z.string().uuid(),
        trashed: z.boolean(),
    }),
    noti_set_database_trash: mcpSourceSchema.extend({ trashed: z.boolean() }),
};
export const mcpDatabaseTools = [
    {
        name: 'noti_list_members',
        scope: 'read',
        description: 'List workspace member ids for person fields. Names require userInfo=noemail or email; emails are returned only with userInfo=email.',
    },
    {
        name: 'noti_get_database',
        scope: 'read',
        description: 'Read a database schema, saved views and rows with stable ids. Each row id is also its page id: use noti_get_page and noti_edit_page for the row body.',
    },
    {
        name: 'noti_update_database',
        scope: 'update',
        description: 'Update a database name/icon, properties or saved views. Property values are keyed by stable property id. Preserve property ids; replacing the schema can remove data. Views support table, board, calendar, list and gallery.',
    },
    {
        name: 'noti_create_database_row',
        scope: 'insert',
        description: 'Create a row with values keyed by property id and optional Markdown page body. Returns its stable row/page id.',
    },
    {
        name: 'noti_update_database_row',
        scope: 'update',
        description: 'Update one or more row values keyed by property id. Unspecified values and the page body are preserved.',
    },
    {
        name: 'noti_set_database_row_trash',
        scope: 'update',
        description: 'Move the specified row to the trash or restore it. Never permanently deletes its page content.',
    },
    {
        name: 'noti_set_database_trash',
        scope: 'update',
        description: 'Move a database to the trash or restore it. Retains its schema, views, row pages and linked references.',
    },
].map((tool) => ({ ...tool, inputSchema: z.toJSONSchema(mcpDatabaseArguments[tool.name]) }));
/** Runtime validation mirrors the tool catalogue; unknown fields fail closed. */
export const mcpToolCallSchema = z.discriminatedUnion('name', [
    z.object({ name: z.literal('noti_get_page_structure'), arguments: mcpPageSchema }),
    z.object({ name: z.literal('noti_edit_page'), arguments: mcpPageEditSchema }),
    z.object({ name: z.literal('noti_get_block_vocabulary'), arguments: mcpWorkspaceSchema }),
    z.object({
        name: z.literal('noti_search'),
        arguments: mcpWorkspaceSchema.extend({
            query: z.string().max(2_000),
            limit: z.number().int().min(1).max(50).default(10),
        }),
    }),
    z.object({
        name: z.literal('noti_get_page'),
        arguments: mcpPageSchema.extend({ format: z.enum(['markdown', 'json']).default('markdown') }),
    }),
    z.object({
        name: z.literal('noti_list_pages'),
        arguments: mcpWorkspaceSchema.extend({
            limit: z.number().int().min(1).max(200).default(50),
            offset: z.number().int().min(0).max(100000).default(0),
        }),
    }),
    z.object({
        name: z.literal('noti_list_databases'),
        arguments: mcpWorkspaceSchema.extend({ includeTrashed: z.boolean().default(false) }),
    }),
    z.object({
        name: z.literal('noti_list_members'),
        arguments: mcpDatabaseArguments.noti_list_members,
    }),
    z.object({
        name: z.literal('noti_get_database'),
        arguments: mcpDatabaseArguments.noti_get_database,
    }),
    z.object({
        name: z.literal('noti_update_database'),
        arguments: mcpDatabaseArguments.noti_update_database,
    }),
    z.object({
        name: z.literal('noti_create_database_row'),
        arguments: mcpDatabaseArguments.noti_create_database_row,
    }),
    z.object({
        name: z.literal('noti_update_database_row'),
        arguments: mcpDatabaseArguments.noti_update_database_row,
    }),
    z.object({
        name: z.literal('noti_set_database_row_trash'),
        arguments: mcpDatabaseArguments.noti_set_database_row_trash,
    }),
    z.object({
        name: z.literal('noti_set_database_trash'),
        arguments: mcpDatabaseArguments.noti_set_database_trash,
    }),
    z.object({ name: z.literal('noti_get_comments'), arguments: mcpPageSchema }),
    z.object({
        name: z.literal('noti_create_page'),
        arguments: mcpWorkspaceSchema.extend({
            ...createPageSchema.shape,
            markdown: mcpMarkdownSchema.optional(),
        }),
    }),
    z.object({
        name: z.literal('noti_update_page'),
        arguments: mcpPageSchema.extend({
            title: createPageSchema.shape.title,
            icon: createPageSchema.shape.icon.nullable(),
            cover: pageCoverSchema.nullable().optional(),
            markdown: mcpMarkdownSchema.optional(),
        }),
    }),
    z.object({
        name: z.literal('noti_create_database'),
        arguments: mcpPageSchema.extend({
            name: z.string().trim().min(1).max(120),
            properties: z
                .array(z
                .object({
                id: propertySchema.shape.id.optional(),
                name: z.string().trim().min(1).max(80),
                type: z.enum([
                    'title',
                    'text',
                    'number',
                    'select',
                    'status',
                    'multi',
                    'person',
                    'relation',
                    'rollup',
                    'date',
                    'checkbox',
                    'url',
                    'email',
                    'phone',
                ]),
                options: z.array(z.string().trim().min(1).max(60)).max(60).optional(),
                relation: propertySchema.shape.relation,
                rollup: propertySchema.shape.rollup,
            })
                .strict())
                .min(1)
                .max(40),
            rows: z
                .array(z.record(z.string().max(80), z.union([z.string().max(10_000), z.number(), z.boolean(), z.array(z.string().max(60))])))
                .max(1_000)
                .optional(),
        }),
    }),
    z.object({
        name: z.literal('noti_upload_file'),
        arguments: mcpPageSchema.extend({
            filename: z.string().trim().min(1).max(255),
            contentType: z.string().trim().min(1).max(128),
            dataBase64: z.string().min(1).max(15_000_000),
        }),
    }),
    z.object({
        name: z.literal('noti_add_comment'),
        arguments: mcpPageSchema.extend(createThreadSchema.shape),
    }),
]);
/** JSON-RPC transport shape; tools validate their own arguments separately. */
export const mcpRpcRequestSchema = z.object({
    jsonrpc: z.literal('2.0'),
    id: z.union([z.string().max(200), z.number().finite(), z.null()]).optional(),
    method: z.string().min(1).max(200),
    params: z.record(z.string(), z.unknown()).optional(),
});
export const mcpRpcToolParamsSchema = z.object({
    name: z.string().min(1).max(200),
    arguments: z.record(z.string(), z.unknown()).default({}),
});
export const mcpToolInputSchemas = new Map(mcpToolCallSchema.options.map((schema) => [
    schema.shape.name.value,
    z.toJSONSchema(schema.shape.arguments),
]));
//# sourceMappingURL=integrations.js.map